import { selectVirtualBackgroundAvailable } from 'features/virtual-backgrounds/selectors';
import React from 'react';
import { Tab, TabPanel } from 'components/Tabs';
import AudioTab from 'features/room/settings/tabs/AudioTab';
import VideoTab from 'features/join/publisher/settings/VideoTab';
import { useAppSelector } from 'store/hooks';
import { useBroadcastSetup } from 'utils/broadcast-setup';
import { useFormContext } from 'react-hook-form';
import SettingsTabs from 'components/SettingsTabs';
import { PreviewFormValues } from 'features/join/publisher/JoinPreview';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import VirtualBackground from './VirtualBackground';

interface SettingsTab {
  id: string;
  label: string;
  panel: React.ReactNode;
}

const Settings = () => {
  const { t } = useTranslation('settings');
  const {
    video,
    audio,
    audioStream,
    videoStream,
    onVideoInputChange,
    onAudioOutputChange,
    onAudioInputChange,
  } = useBroadcastSetup();
  const { control } = useFormContext<PreviewFormValues>();

  const { hasPermissions } = usePermissions();
  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);
  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);
  const VBAvailable = useAppSelector(selectVirtualBackgroundAvailable);

  const showVB = allowVideoBroadcast && VBAvailable;

  const availableTabs: SettingsTab[] = [];

  if (allowVideoBroadcast) {
    availableTabs.push({
      id: 'video',
      label: t('video.tab_label'),
      panel: (
        <VideoTab
          control={control}
          videoStream={videoStream}
          onVideoInputChange={onVideoInputChange}
          cameraDisabled={video.disabled}
        />
      ),
    });
  }

  if (allowAudioBroadcast) {
    availableTabs.push({
      id: 'audio',
      label: t('audio.tab_label'),
      panel: (
        <AudioTab
          vuMeterActive={audio.active}
          onMicChange={onAudioInputChange}
          onOutputChange={onAudioOutputChange}
          micDisabled={audio.disabled}
          control={control}
          audioStream={audioStream}
          allowBroadcast
        />
      ),
    });
  }

  if (showVB) {
    availableTabs.push({
      id: 'vb',
      label: t('virtual_bg.tab_label'),
      panel: <VirtualBackground />,
    });
  }

  return (
    <SettingsTabs
      renderPanels={(activeTab) => (
        <>
          {availableTabs.map((tab, index) => (
            <TabPanel
              key={tab.id}
              value={activeTab}
              index={index}
              id={`${tab.id}-tabpanel`}
              aria-labelledby={`${tab.id}-tab`}
            >
              {tab.panel}
            </TabPanel>
          ))}
        </>
      )}
    >
      {availableTabs.map((tab, index) => (
        <Tab
          key={tab.id}
          label={tab.label}
          value={index}
          id={`${tab.id}-tab`}
          aria-controls={`${tab.id}-tabpanel`}
        />
      ))}
    </SettingsTabs>
  );
};

export default Settings;
